import { useEffect, useState } from "react";
import "./EditableSubmodule.scss";
import { InputText } from "primereact/inputtext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faFlagCheckered, faVideo, faComputerMouse, faFile, faHeadphones } from "@fortawesome/free-solid-svg-icons";
import EditButton from "../EditButton/EditButton";
import ClickBoundary from "../ClickBoundary/ClickBoundary";
import { useEditMode } from "../EditModeContext/EditModeContext";
import { MediaType, PackageTypeToMediaType } from "../../../../Shared/ContentData/PackageType";

const EditableSubmodule = ({ moduleId, moduleType, submodule }) => {
	const { activeField, setActiveField, cancelAll } = useEditMode();

	// local UI-state (do NOT mutate data)
	const [mediaType, setMediaType] = useState(PackageTypeToMediaType[submodule.PackageType] ?? MediaType.Video);
	const [showProgress, setShowProgress] = useState(!!submodule.HasProgress);

	const [drafts, setDrafts] = useState({
		name: submodule.Name ?? "",
		keywords: submodule.Keywords ?? "",
		runtime: submodule.Duration ?? "",
		content: submodule.StoredName ?? "",
		image: submodule.DeviceImageUrl ?? "",
	});

	const mediaIconMap = {
		1: faVideo,
		2: faComputerMouse,
		3: faFile,
		4: faHeadphones,
	};

	// keep drafts/toggles in sync when switching edit targets
	useEffect(() => {
		setDrafts({
			name: submodule.Name ?? "",
			keywords: submodule.Keywords ?? "",
			runtime: submodule.Duration ?? "",
			content: submodule.StoredName ?? "",
			image: submodule.DeviceImageUrl ?? "",
		});
		setMediaType(PackageTypeToMediaType[submodule.PackageType] ?? MediaType.Video);
		setShowProgress(!!submodule.HasProgress);
	}, [submodule.Id, submodule.Name, submodule.Keywords, submodule.Duration, submodule.StoredName, submodule.DeviceImageUrl, submodule.PackageType, submodule.HasProgress, activeField]);

	const isActive = (field) =>
		activeField?.scope === "submodule" &&
		activeField.moduleId === moduleId &&
		activeField.id === submodule.Id &&
		activeField.field === field;

	const onKeyDownSimple = (e) => {
		if (e.key === "Escape") {
			cancelAll();
		}
		if (e.key === "Enter" && e.currentTarget.tagName !== "TEXTAREA") {
			e.preventDefault();
			e.currentTarget.blur();
		}
	};

	return (
		<div className="editable-submodule">
			<div className="editable-submodule-sidebar">
				<div className="editable-submodule-sidebar-item">
					<FontAwesomeIcon
						icon={mediaIconMap[mediaType]}
						style={{ color: "#143872" }}
						className="media-type-icon"
					/>
					<p>Media</p>
					<select
						value={mediaType}
						onChange={(e) => setMediaType(Number(e.target.value))}
						onMouseDown={(e) => e.stopPropagation()}
						className="editable-submodule-sidebar-dropdown"
					>
						<option value={MediaType.Video}>Video</option>
						<option value={MediaType.Interactive}>Interactive</option>
					</select>
				</div>

				<div
					className="editable-submodule-sidebar-item clickable"
					onMouseDown={(e) => e.stopPropagation()}
					onClick={() => setShowProgress((v) => !v)}
				>
					<FontAwesomeIcon
						icon={showProgress ? faFlag : faFlagCheckered}
						style={{ color: showProgress ? "#143872" : "#F68B1F" }}
					/>
					<p>{showProgress ? "Show Progress" : "Hide Progress"}</p>
				</div>

			</div>

			{/* Submodule Name */}
			<div className="editable-submodule-main">
				<div className="editable-submodule-header">
					<ClickBoundary>
						{isActive("name") ? (
							<InputText
								autoFocus
								value={drafts.name}
								onChange={(e) => setDrafts((d) => ({ ...d, name: e.target.value }))}
								onKeyDown={onKeyDownSimple}
								onBlur={cancelAll}
								className="w-full"
							/>
						) : (
							<h3 className="editable-submodule-title">
								{submodule.Name}
								<EditButton
									onClick={() =>
										setActiveField({ scope: "submodule", moduleId, id: submodule.Id, field: "name" })
									}
								/>
							</h3>
						)}
					</ClickBoundary>
				</div>

				{/* Keywords */}
				<div className="editable-submodule-row">
					<h4>Keywords</h4>
					<ClickBoundary>
						{isActive("keywords") ? (
							<InputText
								autoFocus
								value={drafts.keywords}
								onChange={(e) => setDrafts((d) => ({ ...d, keywords: e.target.value }))}
								onKeyDown={onKeyDownSimple}
								onBlur={cancelAll}
								className="w-full"
								placeholder="e.g. Adding, Users, Admin"
							/>
						) : (
							<div className="field-value">
								<span>{submodule.Keywords || "Add keywords"}</span>
								<EditButton
									onClick={() =>
										setActiveField({ scope: "submodule", moduleId, id: submodule.Id, field: "keywords" })
									}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Estimated Runtime */}
				<div className="editable-submodule-row">
					<h4>Estimated Runtime</h4>
					<ClickBoundary>
						{isActive("runtime") ? (
							<InputText
								autoFocus
								value={drafts.runtime}
								onChange={(e) => setDrafts((d) => ({ ...d, runtime: e.target.value }))}
								onKeyDown={onKeyDownSimple}
								onBlur={cancelAll}
								className="w-full"
								placeholder="minutes"
							/>
						) : (
								<div className="field-value">
									<span>{submodule.Duration != null && submodule.Duration !== "" ? `${submodule.Duration} min` : "Not set"}</span>
								<EditButton
									onClick={() =>
										setActiveField({ scope: "submodule", moduleId, id: submodule.Id, field: "runtime" })
									}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Content */}
				<div className="editable-submodule-row">
					<h4>Content</h4>
					<ClickBoundary>
						{isActive("content") ? (
							<InputText
								autoFocus
								value={drafts.content}
								onChange={(e) => setDrafts((d) => ({ ...d, content: e.target.value }))}
								onKeyDown={onKeyDownSimple}
								onBlur={cancelAll}
								className="w-full"
								placeholder='e.g. "adding_users_bt_cloud_work_admin_centre.zip"'
							/>
						) : (
							<div className="field-value field-value--with-action">
									<span>
										Current Content:&nbsp;<strong>{submodule.StoredName || "None"}</strong>
								</span>
								<EditButton
									onClick={() =>
										setActiveField({ scope: "submodule", moduleId, id: submodule.Id, field: "content" })
									}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Device Image */}
				{moduleType === 1 && (
					<div className="editable-submodule-row">
						<h4>Device Image</h4>
						<div className="editable-submodule-row-device-image">
							{submodule.DeviceImageUrl ? (
								<img src={submodule.DeviceImageUrl} alt={submodule.Name} className="editable-submodule-row-device-image-img" />
							) : (
								<span>No Image Set</span>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default EditableSubmodule;
